// src/components/admin/CohortDetail.js - Member list for a single cohort
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import EditCohortUsers from './EditCohortUsers';
import EditCohortCoaches from './EditCohortCoaches';
import './Admin.css';

const CohortDetail = ({ tenantId, cohort, onBack }) => {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [editingUsers, setEditingUsers] = useState(false);
  const [editingCoaches, setEditingCoaches] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [removingCoachId, setRemovingCoachId] = useState(null);

  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    loadDetail();
  }, [cohort.id]);

  const loadDetail = async () => {
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${tenantId}/cohorts/${cohort.id}`, user),
        'Loading cohort...'
      );

      if (data.success) {
        setDetail(data.data);
      }
    } catch (err) {
      console.error('Error loading cohort detail:', err);
    }
  };

  // Instantaneous, single-purpose call - deliberately bypasses useApiCall so
  // removing one member doesn't disable the rest of the page.
  const removeMember = async (userId) => {
    setRemovingUserId(userId);
    try {
      const data = await apiService.delete(
        `/api/v1/tenants/${tenantId}/cohorts/${cohort.id}/members/${userId}`,
        user
      );

      if (data.success) {
        setDetail(prev => ({
          ...prev,
          members: prev.members.filter(m => m.id !== userId)
        }));
      }
    } catch (err) {
      console.error('Error removing member:', err);
    } finally {
      setRemovingUserId(null);
    }
  };

  // Instantaneous, single-purpose call - deliberately bypasses useApiCall so
  // removing one coach doesn't disable the rest of the page.
  const removeCoach = async (userId) => {
    setRemovingCoachId(userId);
    try {
      const data = await apiService.delete(
        `/api/v1/tenants/${tenantId}/cohorts/${cohort.id}/coaches/${userId}`,
        user
      );

      if (data.success) {
        setDetail(prev => ({
          ...prev,
          coaches: prev.coaches.filter(c => c.id !== userId)
        }));
      }
    } catch (err) {
      console.error('Error removing coach:', err);
    } finally {
      setRemovingCoachId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (editingUsers) {
    return (
      <EditCohortUsers
        tenantId={tenantId}
        cohort={cohort}
        onBack={() => {
          setEditingUsers(false);
          loadDetail();
        }}
      />
    );
  }

  if (editingCoaches) {
    return (
      <EditCohortCoaches
        tenantId={tenantId}
        cohort={cohort}
        onBack={() => {
          setEditingCoaches(false);
          loadDetail();
        }}
      />
    );
  }

  return (
    <div className="admin-page">
      <div className="flex flex-between mb-6">
        <div>
          <button onClick={onBack} className="btn btn-secondary btn-sm mb-3">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Cohorts
          </button>
          <h2 className="text-2xl font-semibold text-gray-800">{cohort.name}</h2>
          {cohort.description && (
            <p className="text-sm text-gray-600 mt-1">{cohort.description}</p>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setEditingCoaches(true)}
            className="btn btn-secondary"
            disabled={loading}
          >
            Edit Coaches
          </button>
          <button
            onClick={() => setEditingUsers(true)}
            className="btn btn-primary"
            disabled={loading}
          >
            Edit Cohort Users
          </button>
        </div>
      </div>

      {loading && !detail ? (
        <div className="text-center py-8">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading members...</p>
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <div>
            <strong>Error loading cohort:</strong> {error}
          </div>
          <button onClick={loadDetail} className="btn btn-secondary btn-sm ml-3">
            Try Again
          </button>
        </div>
      ) : !detail ? null : (
        <>
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Coaches ({detail.coaches.length})
            </h3>
            {detail.coaches.length === 0 ? (
              <div className="empty-state card">
                <h3>No coaches assigned</h3>
                <p>Use &quot;Edit Coaches&quot; to assign coaches to this cohort.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {detail.coaches.map(coach => (
                  <div key={coach.id} className="user-card">
                    <div className="flex flex-between items-center">
                      <div>
                        <div className="font-semibold text-gray-800">{coach.displayName || 'No Name'}</div>
                        <div className="text-sm text-gray-600">{coach.email}</div>
                        <div className="text-xs text-gray-500 mt-1">Added {formatDate(coach.addedAt)}</div>
                      </div>
                      <div className="user-actions">
                        <button
                          onClick={() => removeCoach(coach.id)}
                          className="btn btn-secondary btn-sm"
                          disabled={removingCoachId === coach.id}
                        >
                          {removingCoachId === coach.id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Members ({detail.members.length})
            </h3>
            {detail.members.length === 0 ? (
              <div className="empty-state card">
                <h3>No members yet</h3>
                <p>Use &quot;Edit Cohort Users&quot; to add people to this cohort.</p>
                <button onClick={() => setEditingUsers(true)} className="btn btn-primary">
                  Edit Cohort Users
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {detail.members.map(member => (
                  <div key={member.id} className="user-card">
                    <div className="flex flex-between items-center">
                      <div>
                        <div className="font-semibold text-gray-800">{member.displayName || 'No Name'}</div>
                        <div className="text-sm text-gray-600">{member.email}</div>
                        <div className="text-xs text-gray-500 mt-1">Added {formatDate(member.addedAt)}</div>
                      </div>
                      <div className="user-actions">
                        <button
                          onClick={() => removeMember(member.id)}
                          className="btn btn-secondary btn-sm"
                          disabled={removingUserId === member.id}
                        >
                          {removingUserId === member.id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

CohortDetail.propTypes = {
  tenantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cohort: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string
  }).isRequired,
  onBack: PropTypes.func.isRequired
};

export default CohortDetail;
